import { Icon } from "./Icon";
import { cn } from "../lib/cn";
import type { Project } from "../data/portfolio";

interface ProjectSummaryCardProps {
  project: Project;
  onOpen: () => void;
}

/** A project while it is closed: key art and name, with a blurb alongside. */
export function ProjectSummaryCard({
  project,
  onOpen,
}: ProjectSummaryCardProps) {
  const blurb = Array.isArray(project.description)
    ? project.description[0]
    : project.description;

  return (
    <button
      type="button"
      aria-expanded={false}
      onClick={onOpen}
      className="group border-hairline bg-raised ease-standard hover:border-hairline-hover flex w-full cursor-pointer items-center gap-4 rounded-card border p-3 text-left transition duration-300 hover:bg-subtle sm:gap-6 sm:p-4"
    >
      {/* Key art and name take the left half of the card, the blurb the right. */}
      <span className="flex min-w-0 flex-1 items-center gap-4 sm:gap-6 lg:w-1/2 lg:flex-none">
        <span
          className={cn(
            "relative aspect-video w-28 shrink-0 overflow-hidden rounded-xl bg-zinc-950 sm:w-44 lg:w-5/12",
            project.imageFit === "contain" && "p-2 sm:p-3",
          )}
        >
          <img
            src={project.image}
            alt=""
            loading="lazy"
            className={cn(
              "ease-standard size-full transition-transform duration-700 group-hover:scale-[1.04]",
              project.imageFit === "contain"
                ? "object-contain"
                : "object-cover",
            )}
          />
        </span>

        <span className="min-w-0 flex-1">
          <span className="text-accent block text-[0.7rem] font-semibold tracking-eyebrow-sm uppercase sm:text-xs">
            {project.category}
          </span>
          {/* Smaller at lg, where the blurb halves the room for the name. */}
          <span className="text-strong mt-2 block text-lg font-semibold tracking-tight sm:text-2xl lg:text-xl xl:text-2xl">
            {project.title}
          </span>
          {project.role ? (
            <span className="text-muted mt-1.5 hidden text-sm font-medium sm:block">
              {project.role}
            </span>
          ) : null}
        </span>
      </span>

      <span className="text-muted hidden min-w-0 flex-1 text-sm leading-6 lg:block">
        <span className="line-clamp-3">{blurb}</span>
      </span>

      <span
        aria-hidden="true"
        className="border-hairline text-muted group-hover:border-hairline-hover group-hover:text-strong ease-standard mr-1 grid size-9 shrink-0 rotate-90 place-items-center rounded-full border transition duration-200 sm:mr-2 sm:size-10"
      >
        <Icon name="arrow" size={16} />
      </span>
    </button>
  );
}
